import { motion } from "framer-motion";
import { Phone, CheckCircle } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import LeadForm from "@/forms/engine/LeadForm";
import callbackRequest from "@/forms/definitions/callbackRequest";
import GoogleTrustPill from "@/components/GoogleTrustPill";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } };
const CALLBACK_CONTEXT = { entry_point: "booking_page_fallback" };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

export default function BookingPage() {
  return (
    <div>
      <SEOHead title="Book a Free Consultation" description="Book a free, no-obligation consultation with CanDo Payments. Choose a time that works for you — we'll review your payment setup and show you how to save." path="/book" />
      <section className="hero-gradient py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="text-center max-w-2xl mx-auto">
            <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua mb-3">Free Consultation</motion.p>
            <motion.h1 variants={fadeUp} className="font-heading text-4xl sm:text-5xl font-bold text-brand-black tracking-tight leading-tight mb-4">Book a time that works for you</motion.h1>
            <motion.p variants={fadeUp} className="text-lg text-brand-grey leading-relaxed">No obligation. No hard sell. Just a conversation about your payments.</motion.p>
          </motion.div>
        </div>
      </section>
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6 flex justify-center">
            <GoogleTrustPill reassurance="We reply in under 2 business hours" testId="booking-trust-pill" />
          </div>
          <p className="text-center text-base text-brand-grey leading-relaxed mb-6">
            Pick a slot that works for you below. We&apos;ll send a calendar invite and a brief confirmation by email — no obligation, no hard sell.
          </p>
          <div className="bg-white border border-brand-light-grey rounded-2xl shadow-sm overflow-hidden">
            <iframe
              src="https://booknow.candopayments.com/portal-embed#/93421000005069155"
              title="Book a call with CanDo Payments"
              width="100%"
              height="750"
              style={{ border: 0, display: "block" }}
              allowFullScreen
              loading="lazy"
              data-testid="zoho-bookings-iframe"
            />
          </div>
          <div className="mt-12 pt-8 border-t border-brand-light-grey/60">
            <div className="text-center mb-6">
              <h2 className="font-heading text-xl font-bold text-brand-black mb-1">Prefer a call back?</h2>
              <p className="text-sm text-brand-grey">If none of the slots work, leave your details and we&apos;ll call you back.</p>
            </div>
            <div className="bg-white border border-brand-light-grey rounded-2xl shadow-sm p-6 sm:p-8" data-testid="callback-request">
              <LeadForm schema={callbackRequest} context={CALLBACK_CONTEXT} />
            </div>
          </div>
          <div className="mt-8 flex items-center justify-center gap-3 text-sm text-brand-grey">
            <Phone size={14} className="text-brand-aqua" />
            <span>Prefer to call? <a href="tel:+448003682989" className="font-semibold text-brand-black hover:text-brand-aqua transition-colors">0800 368 2989</a></span>
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-4 text-xs text-brand-grey">
            <span className="flex items-center gap-1"><CheckCircle size={12} className="text-brand-aqua" /> Free — no charge</span>
            <span className="flex items-center gap-1"><CheckCircle size={12} className="text-brand-aqua" /> No obligation</span>
            <span className="flex items-center gap-1"><CheckCircle size={12} className="text-brand-aqua" /> Real person, not a chatbot</span>
          </div>
        </div>
      </section>
    </div>
  );
}
